import { useEffect, useState } from 'react';
import {
    Modal,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    cn,
} from "@heroui/react";
import { Copy, Check, AlertCircle, RefreshCw, HelpCircle } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import { useGenerateWhatsAppCode } from '@src/presentation/hooks/users';

interface WhatsAppLinkModalProps {
    isOpen: boolean;
    onClose: () => void;
}

const WHATSAPP_NUMBER = '233542480050';

// WhatsApp icon component for cleaner code
const WhatsAppIcon = ({ className }: { className?: string }) => (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
);

export function WhatsAppLinkModal({
    isOpen,
    onClose,
}: WhatsAppLinkModalProps) {
    const navigate = useNavigate();
    const [copied, setCopied] = useState(false);
    const [expirySeconds, setExpirySeconds] = useState<number | null>(null);

    const {
        mutate: generateCode,
        data,
        isPending,
        isError,
        error,
        reset,
    } = useGenerateWhatsAppCode();

    // Generate WhatsApp URL with pre-filled message
    const getWhatsAppUrl = () => {
        if (!data?.linkCode) return `https://wa.me/${WHATSAPP_NUMBER}`;
        const message = encodeURIComponent(data.linkCode);
        return `https://wa.me/${WHATSAPP_NUMBER}?text=${message}`;
    };

    // Generate code when modal opens
    useEffect(() => {
        if (isOpen) {
            generateCode();
        } else {
            // Reset state when modal closes
            reset();
            setCopied(false);
            setExpirySeconds(null);
        }
    }, [isOpen, generateCode, reset]);

    // Set initial expiry seconds when data arrives
    useEffect(() => {
        if (data?.expiresInSeconds) {
            setExpirySeconds(data.expiresInSeconds);
        }
    }, [data?.expiresInSeconds]);

    // Countdown timer
    useEffect(() => {
        if (expirySeconds === null || expirySeconds <= 0) return;

        const timer = setInterval(() => {
            setExpirySeconds(prev => {
                if (prev === null || prev <= 1) {
                    clearInterval(timer);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(timer);
    }, [expirySeconds]);

    const handleCopyCode = async () => {
        if (!data?.linkCode) return;

        try {
            await navigator.clipboard.writeText(data.linkCode);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy code:', err);
        }
    };

    const handleOpenWhatsApp = () => {
        window.open(getWhatsAppUrl(), '_blank', 'noopener,noreferrer');
    };

    const handleRegenerateCode = () => {
        setCopied(false);
        setExpirySeconds(null);
        generateCode();
    };

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const isExpired = expirySeconds !== null && expirySeconds <= 0;

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            size="md"
            placement="center"
            backdrop="blur"
            motionProps={{
                variants: {
                    enter: {
                        y: 0,
                        opacity: 1,
                        transition: {
                            duration: 0.3,
                            ease: "easeOut",
                        },
                    },
                    exit: {
                        y: -20,
                        opacity: 0,
                        transition: {
                            duration: 0.2,
                            ease: "easeIn",
                        },
                    },
                },
            }}
            classNames={{
                base: 'rounded border border-border shadow-custom',
                header: 'border-b-0 pb-0',
                body: 'py-5',
                footer: 'border-t border-default-100 pt-3',
            }}
        >
            <ModalContent>
                {/* Header */}
                <ModalHeader className="flex flex-col gap-2 pt-5">
                    <div className="flex items-center gap-2.5">
                        <div className="flex items-center justify-center w-9 h-9 rounded-full bg-[#25D366]/10">
                            <WhatsAppIcon className="h-5 w-5 text-[#25D366]" />
                        </div>
                        <h2 className="text-lg font-semibold text-foreground">Link your WhatsApp to PascoHub</h2>
                    </div>
                    <p className="text-sm text-default-500">
                        Link your WhatsApp to access past questions and receive updates directly in chat.
                    </p>
                </ModalHeader>
                <ModalBody>
                    <div className="flex flex-col gap-3">
                        {isPending ? (
                            <div className="flex flex-col items-center justify-center py-6 gap-2">
                                <div className="relative">
                                    <div className="absolute inset-0 rounded-full bg-[#25D366]/20 animate-ping" />
                                    <div className="relative flex items-center justify-center w-10 h-10 rounded-full bg-[#25D366]/10">
                                        <WhatsAppIcon className="h-5 w-5 text-[#25D366]" />
                                    </div>
                                </div>
                                <p className="text-xs text-default-500">Generating code...</p>
                            </div>
                        ) : isError ? (
                            <div className="flex flex-col gap-3">
                                <div className="flex items-center gap-2 p-3 rounded-lg bg-danger-50 dark:bg-danger-900/20">
                                    <AlertCircle className="h-4 w-4 text-danger flex-shrink-0" />
                                    <p className="text-xs text-danger">
                                        {(error as Error)?.message || 'Failed to generate code'}
                                    </p>
                                </div>
                                <Button
                                    size="sm"
                                    variant="flat"
                                    onPress={handleRegenerateCode}
                                    className="rounded-lg"
                                    startContent={<RefreshCw className="h-3.5 w-3.5" />}
                                >
                                    Try Again
                                </Button>
                            </div>
                        ) : data ? (
                            <>
                                {/* Code Display */}
                                <div className={cn(
                                    "flex flex-col items-center gap-2 py-4 px-3 rounded border",
                                    isExpired
                                        ? "bg-danger-50/50 dark:bg-danger-900/10 border-danger-200"
                                        : "bg-[#25D366]/5 border-[#25D366]/20"
                                )}>
                                    <p className="text-[10px] text-default-400 uppercase tracking-wider">Your code</p>
                                    <div className={cn(
                                        "text-2xl font-mono font-bold tracking-[0.2em]",
                                        isExpired ? "text-danger/50 line-through" : "text-foreground"
                                    )}>
                                        {data.linkCode}
                                    </div>
                                    {!isExpired && (
                                        <Button
                                            size="sm"
                                            variant="light"
                                            color={copied ? "success" : "default"}
                                            onPress={handleCopyCode}
                                            className="h-7 rounded-lg text-xs"
                                            startContent={copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                                        >
                                            {copied ? "Copied!" : "Copy"}
                                        </Button>
                                    )}
                                </div>

                                {/* Steps */}
                                <div className="flex flex-col gap-1.5">
                                    <p className="text-xs font-medium text-default-700">How to link</p>
                                    <ol className="text-xs text-default-600 space-y-1 list-decimal list-inside">
                                        <li>Open WhatsApp</li>
                                        <li>Send this code to Pepper Bot</li>
                                        <li>You're all set!</li>
                                    </ol>
                                </div>

                                {/* WhatsApp Button */}
                                {!isExpired && (
                                    <Button
                                        onPress={handleOpenWhatsApp}
                                        className="w-full rounded bg-[#25D366] text-white text-sm"
                                        size="md"
                                        startContent={<WhatsAppIcon className="h-4 w-4" />}
                                    >
                                        Open WhatsApp
                                    </Button>
                                )}

                                {/* Timer & Privacy */}
                                <div className="flex flex-col gap-1 text-xs text-default-500">
                                    {expirySeconds !== null && (
                                        isExpired ? (
                                            <button
                                                onClick={handleRegenerateCode}
                                                className="flex items-center gap-1 text-danger hover:underline"
                                            >
                                                <RefreshCw className="h-3 w-3" />
                                                Code expired · Get new
                                            </button>
                                        ) : (
                                            <p>⏱️ Expires in {formatTime(expirySeconds)}</p>
                                        )
                                    )}
                                    {!isExpired && (
                                        <p className="text-default-400">🔒 We only use your number to recognize you on WhatsApp.</p>
                                    )}
                                </div>
                            </>
                        ) : null}
                    </div>
                </ModalBody>
                <ModalFooter className="flex justify-between">
                    <Button
                        size="sm"
                        variant="light"
                        onPress={onClose}
                        className="rounded-lg text-xs text-default-500"
                    >
                        I'll do this later
                    </Button>
                    <Button
                        size="sm"
                        variant="light"
                        className="rounded-lg text-xs text-default-400"
                        onPress={() => {
                            onClose();
                            navigate('/help');
                        }}
                        startContent={<HelpCircle className="h-3 w-3" />}
                    >
                        Help
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}
